<template>
  <div class="wf-selection-lifecycle-container">
    <div class="wf-selection-lifecycle fh fw">
      <div class="wf-selection-lifecycle-header">
        <div
          v-for="(step, index) in visibleSteps"
          :key="step.id"
          class="wf-selection-lifecycle__item"
        >
          <button
            type="button"
            :class="[
              'wf-selection-lifecycle__node',
              { 'wf-selection-lifecycle__node--active': step.id <= currentStep },
            ]"
            @click="handleStepClick(step.id)"
          >
            {{ step.id }}
          </button>
          <span
            :class="[
              'wf-selection-lifecycle__label',
              { 'wf-selection-lifecycle__label--active': step.id <= currentStep },
            ]"
          >
            {{ step?.name }}
          </span>
          <div
            v-if="index !== visibleSteps.length - 1"
            :class="[
              'wf-selection-lifecycle__line',
              { 'wf-selection-lifecycle__line--active': step.id < currentStep },
            ]"
          ></div>
        </div>
      </div>

      <div class="wf-selection-lifecycle__content-stage">
        <div :key="stepContentKey" class="wf-selection-lifecycle__pane">
          <template v-if="currentStep === 1">
            <!-- manual or system flow conformation start  -->
            <div class="wf-selection-lifecycle-confirmation">
              <h4 class="wf-selection-lifecycle-confirmation__title">How would you like to continue?</h4>
              <p class="wf-selection-lifecycle-confirmation__subtitle">
                Choose whether to proceed with manual status change or workflow-based status change.
              </p>
              <div class="wf-selection-lifecycle-confirmation__options">
                  <button
                  type="button"
                  :class="[
                    'wf-selection-lifecycle-confirmation__option',
                    { 'wf-selection-lifecycle-confirmation__option--active': statusChangeMode === 'workflow' }
                  ]"
                  @click="setStatusChangeMode('workflow')"
                >
                  Workflow Based
                </button>
                <button
                  type="button"
                  :class="[
                    'wf-selection-lifecycle-confirmation__option',
                    { 'wf-selection-lifecycle-confirmation__option--active': statusChangeMode === 'manual' }
                  ]"
                  @click="setStatusChangeMode('manual')"
                  :disabled="!showManualStatusChange"
                >
                  Manual Status Change
                </button>
              </div>
            </div>
            <!-- manual or system flow conformation   end -->

            <div class="wf-selection-lifecycle__content-inline">
              <transition name="wf-toggle" mode="out-in">
                <div v-if="statusChangeMode === 'manual'" key="manual-pane">
                  <!--  action- buttons start -->
                  <slot
                    name="actions"
                    :statusChangeMode="statusChangeMode"
                  ></slot>
                  <!-- action buttons end  -->
                </div>
                <div v-else key="workflow-pane">
                    <skeleton v-if="workflowLoading" :rows="3" />
                    <workflows-listing
                    v-else
                    class="wf-selection-lifecycle__listing"
                    :workflows="workflows"
                    :selected-workflow-id="selectedWorkflow.template_id"
                    :loading="workflowLoading"
                    :empty-text="'No workflows available.'"
                    :panelStyle="panelStyle"
                    @select-workflow="handleWorkflowSelect"
                  />
                </div>
              </transition>
            </div>
          </template>

          <template v-else>
            <workflow-starting-stp-selection
              :wftStartStages="selectedWorkflow.start_steps"
              :selectedStartingStep="selectedStartingStep"
              :templateId="selectedWorkflow.template_id"
              :templateVersionId="selectedWorkflow.template_version_id"
              :wfTypeName="selectedWorkflow.template_type_value"
              :selectedUserGroup="selectedWorkflow?.user_group?.core_user_group_members"
              :feature="'life-cycle'"
              :panelStyle="panelStyle"
              @handleUserSelection="handleUserSelection"
              @selectStartingStep="addSelectedStep"
              @removeSelection="removeSelectedStep"
            />
          </template>
        </div>
      </div>

      <div v-if="currentStep === 3" class="v-center flex-end gap-1 py-2">
        <button class="btn cancel" @click="cancelLifeCycle">Cancel</button>
        <button class="btn" @click="startLifecycle">Initiate Workflow</button>
      </div>
    </div>
  </div>
</template>

<script>
import workflowsListing from './workflowsListing.vue'
import WorkflowStartingStpSelection from '../form/workflowStartingStpSelection.vue'
import { StartLifecycleWorkflow } from '@/api'
import { alert, success } from '@/plugins/notification'
import { mapGetters } from 'vuex'
import Skeleton from '@/components/common/skeleton.vue'

export default {
  name: 'wfSelectionLifecycle',
  components: {
    workflowsListing,
    WorkflowStartingStpSelection,
    Skeleton
  },
  props: {
    workflows: {
      type: Array,
      default: () => []
    },
    workflowLoading: {
      type: Boolean,
      default: false
    },
    feature: {
      type: Object,
      require: true
    },
    wfModalLoaderController: {
      type: Function,
      default: null
    },
    cancel: {
      type: Function,
      default: null
    },
    emitChanges: {
      type: Function,
      default: null
    },
    panelStyle: {
      type: String
    },
    showManualStatusChange: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      selectedWorkflow: {},
      currentStep: 1,
      selectedStartingStep: null,
      statusChangeMode: 'workflow',
      steps: [
        { id: 1, name: 'Select Workflow' },
        { id: 2, name: 'Select Starting Step' },
        { id: 3, name: 'Start Workflow' }
      ],
      wFStartStepUsers: [] // thisis to store the users selected for the next step

    }
  },
  computed: {
    ...mapGetters(['isTenantAdmin']),
    visibleSteps () {
      if (this.statusChangeMode === 'manual') {
        return [{ id: 1, name: 'Manual Change' }]
      }
      return this.steps
    },
    stepContentKey () {
      return this.currentStep === 1
        ? `step-1-${this.statusChangeMode}`
        : `step-${this.currentStep}`
    }
  },
  methods: {
    handleStepClick (stepId) {
      if (stepId <= this.currentStep) {
        if (
          this.currentStep === 3 &&
          stepId === 2 &&
          this.selectedWorkflow.start_steps?.length === 1
        ) {
        } else if (
          this.currentStep === 3 &&
          this.selectedWorkflow.start_steps?.length > 1
        ) {
          this.selectedStartingStep = null
          this.currentStep = stepId
        } else if (
          this.currentStep === 3 &&
          stepId === 2 &&
          this.selectedWorkflow.start_steps?.length <= 1
        ) {
          this.selectedStartingStep = null
        } else {
          this.selectedStartingStep = null
          this.currentStep = stepId
        }
      }
    },
    handleWorkflowSelect (workflow) {
      this.selectedWorkflow = workflow
      if (this.selectedWorkflow.start_steps?.length === 1) {
        this.selectedStartingStep = this.selectedWorkflow.start_steps[0]
        this.currentStep = 3
        return
      }
      this.currentStep = 2
    },
    addSelectedStep (stepData) {
      this.selectedStartingStep = stepData
      this.currentStep = 3
    },
    removeSelectedStep () {
      this.selectedStartingStep = null
      if (this.selectedWorkflow.start_steps?.length > 1) {
        this.currentStep = 2
      } else {
        this.currentStep = 1
      }
    },
    cancelLifeCycle () {
      this.cancel()
    },
    startLifecycle () {
      const workflowStepAssignees = this.wFStartStepUsers.map((item) => {
        return ({ user_id: item.user_id, target_tenant_id: item.target_tenant_id })
      })
      const body = {
        bomId: null,
        materialId: null,
        documentId: null,
        stepId: this.selectedStartingStep.id,
        templateId: this.selectedWorkflow.template_id,
        assignees: workflowStepAssignees
      }
      switch (this.feature.name) {
      case 'material':
        body.materialId = this.feature.id
        break
      case 'documents':
        body.documentId = this.feature.id
        break
      case 'bom':
        body.bomId = this.feature.id
        break

      default: {
        alert('Unable to idnetify the module, Please try again!')
        return
      }
      }
      this.wfModalLoaderController({ start: true })
      StartLifecycleWorkflow(body)
        .then((res) => {
          this.wfModalLoaderController({ stop: true })
          success(res.start_lifecycle_workflow.message)
          this.cancelLifeCycle()
          this.feature.data.ongoing_lifecycle_workflow_id = res.start_lifecycle_workflow.workflow_instance_id
          if (typeof this.emitChanges === 'function') {
            this.emitChanges(this.feature?.data?.index, this.feature?.data)
          }
        })
        .catch(() => {
          this.wfModalLoaderController({ stop: true })
          alert('Something went wrong')
        })
    },
    setStatusChangeMode (mode) {
      this.statusChangeMode = mode
      this.selectedWorkflow = {}
      this.selectedStartingStep = null
      this.currentStep = 1
    },
    handleUserSelection (data) {
      this.wFStartStepUsers = data.value.insert
    },
    reset () {
      this.selectedWorkflow = {}
      this.currentStep = 1
      this.selectedStartingStep = null
      this.statusChangeMode = 'workflow'
      this.wFStartStepUsers = []
    }
  },
  watch: {
    'feature.id': {
      handler () {
        this.reset()
      },
      deep: true
    }
  }
}
</script>

<style lang="scss" scoped>
.wf-selection-lifecycle-confirmation {
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 12px;

  &__title {
    margin: 0;
    color: #1e293b;
    font-size: var(--text-s);
    font-weight: 600;
  }

  &__subtitle {
    margin: 6px 0 0;
    color: #64748b;
    font-size: var(--text-xs1);
  }

  &__options {
    margin-top: 10px;
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    & >button {
      flex-grow: 1;
    }
  }

  &__option {
    border: 1px solid #cbd5e1;
    background: #ffffff;
    color: #334155;
    border-radius: 6px;
    padding: 6px 10px;
    cursor: pointer;
    font-size: var(--text-xs2);
    transition: all 0.2s ease;
  }

  &__option:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.08);
  }

  &__option:disabled,
  &__option:disabled:hover {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
    background: #f1f5f9;
    color: #94a3b8;
    border-color: #e2e8f0;
  }

  &__option--active {
    border-color: #84a8f5;
    background: #eff6ff;
    color: #1d4ed8;
  }

}

.wf-selection-lifecycle {
  align-self: baseline;
  display: flex;
  flex-direction: column;
  width: 100%;

  &-header {
    align-self: baseline;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    width: 100%;
    margin: 6px 0 12px;
    overflow-x: auto;
    overflow-y: hidden;
    padding-bottom: 2px;
  }

  &__listing {
    margin-top: 6px;
  }

  &__content-stage {
    position: relative;
    min-height: 220px;
  }

  &__content-inline {
    position: relative;
    min-height: 150px;
  }

  &__pane {
    width: 100%;
  }

  &__item {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    flex: 1;
    min-width: 130px;
    position: relative;
  }

  &__node {
    width: 30px;
    height: 30px;
    min-width: 30px;
    border-radius: 50%;
    border: 1px solid #cbd5e1;
    background: #ffffff;
    color: #475569;
    font-size: var(--text-s);
    font-weight: 600;
    line-height: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    opacity: 0.5;
    transition: all 0.2s ease;
  }

  &__node--active {
  background-color: var(--brand-color);
  border-color:var(--brand-color);
    color: #646464;
    opacity: 1;
  }

  &__label {
    margin-top: 8px;
    font-size: var(--text-xs2);
    color: #94a3b8;
    font-weight: 500;
    text-align: center;
    white-space: nowrap;
  }

  &__label--active {
    color: #373c45;
  }

  &__line {
    height: 1px;
    width: calc(100% - 44px);
    min-width: 88px;
    background: #dbe2ea;
    position: absolute;
    top: 15px;
    opacity: 0.5;
    left: calc(50% + 22px);
  }

  &__line--active {
    background: var(--brand-color);
  }
}

.wf-toggle-enter-active,
.wf-toggle-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.wf-toggle-enter,
.wf-toggle-leave-to {
  opacity: 0;
  transform: translateY(6px);
}

@media (max-width: 768px) {
  .wf-selection-lifecycle {
    &__item {
      min-width: 110px;
    }
  }
}
</style>
